import { useEffect, useState } from "react";
import type { PointerEvent } from "react";
import styled from "@emotion/styled";
import { theme } from "../../lib/theme";
import type { TxFacet } from "../../lib/api";
import type { TxFilterDimensionEntry } from "@clustec/common/api-schemas";
import { Input } from "../ui";
import { formatDimValue, isValidRangeValue, type TxRange } from "../../lib/tx-filters";
import { bucketAtX, brushToRange, rangeToBrush, intToParam } from "../../lib/histogram-brush";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { stackOnSqrtAxis } from "../../lib/view-insights";

/**
 * One filter dimension: a histogram of the network-wide distribution that the
 * user drags across to select a range, kept in sync with a min/max input
 * pair. The brush commits once, on pointer-up; typed values commit debounced.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  min-width: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${theme.spacing.xs};
`;

const Label = styled.span`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  padding: 0 2px;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.sm};
  line-height: 1;

  &:hover {
    color: ${theme.colors.text};
  }
`;

const Plot = styled.div<{ interactive: boolean }>`
  display: flex;
  align-items: flex-end;
  gap: 1px;
  height: 48px;
  border-bottom: 1px solid ${theme.colors.border};
  cursor: ${(p) => (p.interactive ? "crosshair" : "default")};
  /* The brush owns horizontal drags — without this a phone scrolls the page instead. */
  touch-action: none;
  user-select: none;
`;

/**
 * One bucket: a full-height slot holding the network-wide backdrop bar and,
 * when other filters are active, the filtered-count overlay on top of it.
 * The overlay can never exceed the backdrop — the filtered set is a subset.
 */
const Column = styled.div`
  position: relative;
  flex: 1;
  min-width: 1px;
  height: 100%;
`;

/**
 * One bar layer. Height is sqrt-scaled by the knob (heavy-tailed counts
 * would otherwise flatten everything but the mode), with a 2px floor so
 * "rare but present" stays visible. The transition is load-bearing: on real
 * data many buckets change only slightly under a filter, and the movement is
 * what makes the recompute legible.
 */
const Layer = styled.div<{ h: number; tone: string }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${(p) => (p.h > 0 ? `max(${p.h}%, 2px)` : "0")};
  background: ${(p) => p.tone};
  border-radius: 1px 1px 0 0;
  transition:
    height 0.25s ease,
    background 0.25s ease;
`;

/**
 * One selected privacy set's slice of a bar, stacked from the bottom on the
 * bars' own sqrt axis (see stackOnSqrtAxis).
 */
const Segment = styled.div<{ bottom: number; h: number; tone: string }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${(p) => p.bottom}%;
  height: ${(p) => (p.h > 0 ? `max(${p.h}%, 1px)` : "0")};
  background: ${(p) => p.tone};
  transition:
    height 0.25s ease,
    bottom 0.25s ease;
`;

const NoData = styled.div`
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid ${theme.colors.border};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const InputRow = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};

  input {
    padding: 2px ${theme.spacing.xs};
    font-size: ${theme.fontSize.xs};
    font-family: monospace;
  }
`;

const StatLine = styled.div`
  font-size: ${theme.fontSize.xs};
  font-family: monospace;
  color: ${theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Deterministic placeholder silhouette while the facet loads. */
const SKELETON_HEIGHTS = [18, 42, 70, 100, 78, 52, 38, 26, 20, 14, 10, 8, 12, 6, 4, 6];

export interface FilterKnobProps {
  dim: TxFilterDimensionEntry;
  /**
   * The distribution to draw. With filters active this is the filtered facet
   * (recomputed from the remaining txs, this dimension's own range excluded
   * server-side); otherwise the network-wide one. Same shape either way —
   * the knob just renders what it's handed.
   */
  facet: TxFacet | undefined;
  /** True when facet describes the filtered view — switches the stat line to "in view". */
  filtered?: boolean;
  range: TxRange | undefined;
  onChange: (range: TxRange | null) => void;
  /**
   * A fixed picture of the distribution (the saved-view page): no brush, no
   * inputs, just the bars with the committed range highlighted and the stats.
   */
  readOnly?: boolean;
  /**
   * Selected privacy sets to stack inside the bars (read-only use): each
   * set's per-bucket counts, on the same buckets as `facet`. While any are
   * present the rest of the view drops to neutral grey so only the sets carry
   * colour.
   */
  overlays?: Array<{ label: string; color: string; counts: number[] }>;
}

export function FilterKnob({ dim, facet, filtered, range, onChange, readOnly, overlays }: FilterKnobProps) {
  const [drag, setDrag] = useState<[number, number] | null>(null);
  const [minText, setMinText] = useState(range?.min ?? "");
  const [maxText, setMaxText] = useState(range?.max ?? "");

  // Re-seed the inputs whenever the committed range changes from outside the
  // inputs themselves — a brush, a cleared chip, back/forward navigation.
  useEffect(() => {
    setMinText(range?.min ?? "");
    setMaxText(range?.max ?? "");
  }, [range?.min, range?.max]);

  const commitTexts = (minT: string, maxT: string) => {
    const min = isValidRangeValue(minT) ? minT : undefined;
    const max = isValidRangeValue(maxT) ? maxT : undefined;
    if (min === range?.min && max === range?.max) return;
    if (min === undefined && max === undefined) {
      if (range) onChange(null);
      return;
    }
    // Mid-edit contradiction (min above max) — hold until the pair makes sense.
    if (min !== undefined && max !== undefined && BigInt(min) > BigInt(max)) return;
    onChange({ min, max });
  };

  const debouncedTexts = useDebouncedValue(`${minText} ${maxText}`);
  useEffect(() => {
    const [minT, maxT] = debouncedTexts.split(" ");
    commitTexts(minT, maxT);
    // Deps are just the debounced pair: re-running on range identity would
    // double-commit stale text right after an external range change.
  }, [debouncedTexts]);

  const edges = facet?.edges ?? [];
  const counts = facet?.counts ?? [];
  // A filtered view that matches zero rows still has network-wide edges —
  // render the empty plot (and keep it brushable) rather than "no data".
  const hasData = facet !== undefined && edges.length >= 2;
  const n = counts.length;

  const bucketFromEvent = (e: PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return bucketAtX(e.clientX - rect.left, rect.width, n);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!hasData) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const i = bucketFromEvent(e);
    setDrag([i, i]);
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    setDrag([drag[0], bucketFromEvent(e)]);
  };

  const onPointerUp = () => {
    if (!drag) return;
    const selected = brushToRange(edges, drag[0], drag[1]);
    setDrag(null);
    if (selected) {
      onChange({ min: intToParam(selected.min), max: intToParam(selected.max) });
    }
  };

  // While dragging show the live span; otherwise derive it from the committed
  // range. Number() on a wei string loses precision, but only in the highlight
  // — the range itself is passed through as the exact string.
  const selection = drag
    ? ([Math.min(drag[0], drag[1]), Math.max(drag[0], drag[1])] as [number, number])
    : rangeToBrush(
        edges,
        range?.min !== undefined ? Number(range.min) : undefined,
        range?.max !== undefined ? Number(range.max) : undefined,
      );

  const maxCount = counts.reduce((a, b) => Math.max(a, b), 0);
  const sqrtMax = Math.sqrt(maxCount);

  const fmt = (v: number | null) => (v == null ? "-" : formatDimValue(dim.key, v));

  const hasOverlays = !!overlays && overlays.length > 0;
  // Hover text for a bar on the read-only page: the bucket, its count, and each selected set's part.
  const bucketTitle = (i: number, total: number, parts: number[]) => {
    const lo = edges[i];
    const hi = edges[i + 1];
    const span = hi - lo <= 1 && Number.isInteger(lo) ? fmt(lo) : `${fmt(lo)}–${fmt(hi)}`;
    const lines = [`${span}: ${total.toLocaleString()} txs`];
    parts.forEach((n, k) => lines.push(`${overlays![k].label}: ${n.toLocaleString()}`));
    return lines.join("\n");
  };

  return (
    <Wrapper>
      <Header>
        <Label title={dim.label}>{dim.label}</Label>
        {range && !readOnly && <ClearButton onClick={() => onChange(null)} title="Clear filter">×</ClearButton>}
      </Header>

      {facet === undefined ? (
        <Plot interactive={false} style={{ opacity: 0.35 }}>
          {SKELETON_HEIGHTS.map((h, i) => (
            <Column key={i}>
              <Layer h={h} tone={theme.colors.border} />
            </Column>
          ))}
        </Plot>
      ) : !hasData ? (
        <NoData>no data</NoData>
      ) : (
        <Plot
          interactive={!readOnly}
          onPointerDown={readOnly ? undefined : onPointerDown}
          onPointerMove={readOnly ? undefined : onPointerMove}
          onPointerUp={readOnly ? undefined : onPointerUp}
        >
          {counts.map((c, i) => {
            // Read-only with no range of its own, every bar is matching txs — all of it is "the view".
            const inSelection =
              selection !== null ? i >= selection[0] && i <= selection[1] : !!readOnly && !range;
            const barPct = sqrtMax > 0 ? (Math.sqrt(c) / sqrtMax) * 100 : 0;
            const viewTone = hasOverlays ? `${theme.colors.textMuted}70` : theme.colors.primary;
            const parts = hasOverlays ? overlays.map((o) => o.counts[i] ?? 0) : [];
            const segments = stackOnSqrtAxis(parts, c, sqrtMax);
            return (
              <Column key={i} title={readOnly ? bucketTitle(i, c, parts) : undefined}>
                <Layer h={barPct} tone={inSelection ? viewTone : theme.colors.border} />
                {segments.map((seg, k) => (
                  <Segment key={k} bottom={seg.bottom} h={seg.h} tone={overlays![k].color} />
                ))}
              </Column>
            );
          })}
        </Plot>
      )}

      {!readOnly && (
        <InputRow>
          <Input
            type="text"
            inputMode="numeric"
            placeholder="min"
            value={minText}
            onChange={(e) => setMinText(e.target.value.trim())}
            onBlur={() => commitTexts(minText, maxText)}
            onKeyDown={(e) => e.key === "Enter" && commitTexts(minText, maxText)}
          />
          <Input
            type="text"
            inputMode="numeric"
            placeholder="max"
            value={maxText}
            onChange={(e) => setMaxText(e.target.value.trim())}
            onBlur={() => commitTexts(minText, maxText)}
            onKeyDown={(e) => e.key === "Enter" && commitTexts(minText, maxText)}
          />
        </InputRow>
      )}

      {facet && (
        <StatLine>
          {filtered && (
            <span style={{ color: theme.colors.primary }}>{facet.nonNullCount.toLocaleString()} in view · </span>
          )}
          p50 {fmt(facet.p50)} · p95 {fmt(facet.p95)} · max {fmt(facet.max)}
          {!filtered && dim.nullable && facet.nonNullCount > 0 && (
            <> · {facet.nonNullCount.toLocaleString()} set</>
          )}
        </StatLine>
      )}
    </Wrapper>
  );
}
